<template>
    <section class="max-w-5xl mx-auto my-40 p-8">
        <!-- Titre -->
        <header class="text-center mb-12">
            <h1 class="text-3xl font-bold mb-3">Bonne nouvelle, votre vitrine est prête !</h1>
            <p class="text-gray-600 text-lg">
                Choisissez votre abonnement simple et flexible pour la rendre accessible au public.
            </p>
        </header>

        <!-- Avantages -->
        <div class="grid md:grid-cols-2 gap-6 mb-16">
            <div class="flex items-start space-x-3">
                <span class="text-violet-600 text-xl">✅</span>
                <p class="text-gray-700">Hébergement sécurisé & HTTPS inclus</p>
            </div>
            <div class="flex items-start space-x-3">
                <span class="text-violet-600 text-xl">✅</span>
                <p class="text-gray-700">Mises à jour illimitées de votre vitrine</p>
            </div>
            <div class="flex items-start space-x-3">
                <span class="text-violet-600 text-xl">✅</span>
                <p class="text-gray-700">Support client rapide en cas de besoin</p>
            </div>
            <div class="flex items-start space-x-3">
                <span class="text-violet-600 text-xl">✅</span>
                <p class="text-gray-700">Annulable à tout moment, sans frais cachés</p>
            </div>
        </div>

        <!-- Cards -->
        <div class="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <!-- Mensuel -->
            <div class="bg-white border rounded-lg shadow-md p-8 flex flex-col justify-between text-center">
                <div>
                    <h3 class="text-lg font-semibold mb-4">Abonnement mensuel</h3>
                    <p class="text-4xl font-bold text-violet-600 mb-2">5,99€<span
                            class="text-lg font-medium">/mois</span></p>
                </div>
                <button @click="subscribe('mensuel')"
                    class="mt-6 w-full bg-violet-600 hover:bg-violet-700 text-white font-semibold py-3 rounded-lg transition"
                    :disabled="loading">
                    {{ loading && currentPlan === 'mensuel' ? 'Chargement...' : 'Choisir le mensuel' }}
                </button>
            </div>

            <!-- Annuel -->
            <div
                class="bg-white border-2 border-violet-500 rounded-lg shadow-lg p-8 flex flex-col justify-between text-center relative">
                <span
                    class="absolute top-3 right-3 text-xs bg-violet-600 text-white px-2 py-1 rounded-full">Économisez</span>
                <div class="mt-4">
                    <h3 class="text-lg font-semibold mb-4">Abonnement annuel</h3>
                    <p class="text-4xl font-bold text-violet-600 mb-2">59,99€<span
                            class="text-lg font-medium">/an</span></p>
                    <p class="text-sm text-gray-500">Soit <strong>5€/mois</strong></p>
                </div>
                <button @click="subscribe('annuel')"
                    class="mt-6 w-full bg-violet-100 hover:bg-violet-200 text-violet-700 font-semibold py-3 rounded-lg border border-violet-300 transition"
                    :disabled="loading">
                    {{ loading && currentPlan === 'annuel' ? 'Chargement...' : 'Choisir l’annuel' }}
                </button>
            </div>
        </div>

        <!-- Case CGV -->
        <div class="mt-4 text-sm text-gray-700 flex items-center justify-center gap-2">
            <input type="checkbox" id="acceptCgv" v-model="acceptedCgv" class="h-4 w-4">
            <label for="acceptCgv">
                J'ai lu et j'accepte les <a href="/cgv" class="text-violet-600 underline" target="_blank">CGV</a>.
            </label>
        </div>

        <p class="text-sm text-gray-500 mt-6 text-center">Paiement 100% sécurisé via Stripe</p>

        <!-- FAQ -->
        <div class="border-t pt-10 mt-16">
            <h2 class="text-xl font-semibold mb-6 text-center">Questions fréquentes</h2>
            <div class="space-y-4 text-gray-600 max-w-lg mx-auto">
                <p>
                    <strong class="text-gray-800">⏳ Puis-je annuler ?</strong><br />
                    Oui, l’abonnement est sans engagement. Vous pouvez résilier à tout moment depuis votre profil.
                </p>
                <p>
                    <strong class="text-gray-800">🔄 Puis-je changer de formule ?</strong><br />
                    Bien sûr, vous pouvez passer du mensuel à l’annuel (ou inversement) quand vous voulez.
                </p>
                <p>
                    <strong class="text-gray-800">🔒 Le paiement est-il sécurisé ?</strong><br />
                    Oui, nous utilisons Stripe, une solution reconnue pour la sécurité des transactions.
                </p>
            </div>
        </div>
    </section>

    <!-- Modal erreur -->
    <div v-if="showError" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
        <div class="bg-white rounded p-8 max-w-sm w-full text-center shadow-lg" role="alert" aria-live="assertive">
            <h2 class="text-2xl font-bold mb-4 text-red-600">Erreur</h2>
            <p class="mb-6 text-gray-700">
                {{ errorMessage }}
            </p>
            <button @click="showError = false"
                class="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition duration-200">
                Fermer
            </button>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';

const showError = ref(false)
const errorMessage = ref('')
const acceptedCgv = ref(false)

const store = useStore();
const token = localStorage.getItem('token');
const user = computed(() => store.getters.getUser);
const baseUrl = process.env.VUE_APP_BASE_URL

const loading = ref(false);
const currentPlan = ref(null);

async function subscribe(planType) {

    if (!user.value) {
        errorMessage.value = "Vous devez être connecté pour vous abonner."
        showError.value = true
        return
    }

    if (!acceptedCgv.value) {
        errorMessage.value = "Vous devez accepter les CGV pour continuer."
        showError.value = true
        return
    }

    loading.value = true;
    currentPlan.value = planType;

    try {
        const response = await fetch(`${baseUrl}/api/stripe/create-checkout-session`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                userId: user.value.id,
                userEmail: user.value.email,
                planType,
            }),
        });

        if (!response.ok) {
            throw new Error('Erreur lors de la création de la session Stripe');
        }

        const data = await response.json();

        // Redirige vers Stripe Checkout
        window.location.href = data.url;
    } catch (error) {
        alert(error.message || 'Une erreur est survenue');
    } finally {
        loading.value = false;
        currentPlan.value = null;
    }
}
</script>
